<template>
  <v-row
    no-gutters
    :style="{ border: '1px solid #cacaca', borderRadius: '5px' }"
    class="px-4 py-6 mb-6 pa-lg-6"
  >
    <v-col>
      <v-row no-gutters>
        <v-col>
          <h2
            class="fs-18 font-weight-medium"
            style="font-family: Inter, Roboto, Arial, sans-serif"
          >
            <template
              v-if="
                activePricing?.type !== 'regular' && !ticket.is_paused && isTicketDateValid(ticket)
              "
            >
              <span>{{ ticket?.name }}</span>
              <span class="ml-2" style="text-decoration: line-through">
                {{ Currency.formatCents(activePricing.originalPrice) }}
              </span>
              <span class="ml-2 font-weight-bold">
                {{ Currency.formatCents(activePricing.price) }}
              </span>
              <v-chip size="small" class="ml-2" variant="outlined" color="#F36E21">{{
                activePricing.label
              }}</v-chip>
            </template>
            <span v-else>
              {{ displayTicketName(ticket) }}
            </span>
            <v-chip v-if="isSoldOut" size="small" class="ml-2">Sold Out</v-chip>
            <v-chip v-else-if="!isTicketDateValid(ticket)" size="small" color="error" class="ml-2"
              >Not Available</v-chip
            >
          </h2>
          <!-- <div class="mt-2 d-flex flex-wrap ga-2">
            <v-chip
              v-for="(includes, k) of ticket.included"
              :color="eventData?.event_color || 'info'"
              variant="tonal"
              density="compact"
              :key="k"
            >
              <span class="fs-12">{{ includes }}</span>
            </v-chip>
          </div> -->
        </v-col>
      </v-row>

      <v-divider class="my-4"></v-divider>

      <template v-if="isTicketDateValid(ticket) && !ticket.is_paused && !isSoldOut">
        <v-row no-gutters v-if="ticket.description">
          <v-col>
            <v-btn
              variant="text"
              :prepend-icon="showDescription ? 'mdi-chevron-up' : 'mdi-chevron-down'"
              @click="showDescription = !showDescription"
              density="compact"
              class="pa-0 mb-2 fs-12"
              style="
                text-transform: none;
                color: #737373;
                font-weight: normal;
                letter-spacing: normal;
              "
            >
              {{ showDescription ? 'Show less details' : 'Show more details' }}
            </v-btn>

            <v-expand-transition>
              <div v-if="showDescription">
                <p class="fs-14" style="color: #737373">
                  {{ ticket.description }}
                </p>
              </div>
            </v-expand-transition>
          </v-col>
        </v-row>

        <v-row
          no-gutters
          v-if="
            ticket.table_id &&
            ticket.included_ticket_count !== undefined &&
            ticket.included_ticket_count !== null &&
            ticket.included_ticket_count > 0
          "
          class="mt-2"
        >
          <v-col v-if="ticket.included_ticket_count > 0 && qty > 0">
            <p class="fs-12 font-weight-bold font-italic">
              Includes {{ ticket.included_ticket_count * qty }} seats
            </p>
          </v-col>
        </v-row>

        <v-row no-gutters justify="end">
          <!-- Left Side - display active ticket price -->
          <v-col v-if="eventData?.is_paid_event">
            <p class="fs-16" style="color: var(--fundeasy-colors-neutral-700)">
              {{ Currency.formatCents(activePricing.price) }}
            </p>
          </v-col>

          <v-col>
            <div class="d-flex align-center justify-end">
              <v-btn
                icon
                color="#0E6CB3"
                size="larger"
                variant="text"
                :disabled="localQty <= (ticket?.min_per_order ?? 0)"
                @click="decrementQty"
              >
                <v-icon>mdi-minus</v-icon>
              </v-btn>
              <span
                class="mx-2 fs-16 font-weight-medium border border-sm border-solid px-6 py-2"
                style="text-align: center"
              >
                {{ localQty }}
              </span>
              <v-btn
                icon
                color="#0E6CB3"
                size="larger"
                variant="text"
                :disabled="
                  localQty >= (ticket?.max_per_order ?? Number.MAX_SAFE_INTEGER) ||
                  localQty >= (getRemainingHosts(ticket) ?? Number.MAX_SAFE_INTEGER) ||
                  (!ticket?.table_id &&
                    localQty >= (ticket?.max_quantity ?? Number.MAX_SAFE_INTEGER))
                "
                @click="incrementQty"
              >
                <v-icon>mdi-plus</v-icon>
              </v-btn>
            </div>
          </v-col>
        </v-row>
      </template>

      <template v-else>
        <v-sheet color="#FEF2F2" class="pa-1 text-center">
          <span class="fs-16 font-weight-medium">Unavailable</span>
        </v-sheet>
      </template>
    </v-col>
  </v-row>
</template>

<script setup>
import { DateTime } from 'luxon';
import { computed, ref, watch } from 'vue';
import { Currency } from '~/utils/currency';

const props = defineProps({
  ticket: Object,
  selectedTickets: Object,
  eventData: Object,
  qty: { type: Number, default: 0 },
});

const emit = defineEmits(['update:qty']);

const localQty = ref(props.qty);

watch(
  () => props.qty,
  (newPropQty) => {
    localQty.value = newPropQty;
  },
);

const showDescription = ref(false);

const activePricing = computed(() => getActiveTicketPricing(props.ticket));

function getActiveTicketPricing(ticket) {
  const regularPrice = Number(
    ticket?.regular_price ?? ticket?.config?.regular_price ?? ticket?.config?.price ?? 0,
  );

  if (ticket?.is_early_bird && ticket?.is_early_bird_active) {
    return {
      price: Number(ticket?.early_bird_amount ?? ticket?.config?.price ?? regularPrice),
      originalPrice: regularPrice,
      type: 'early_bird',
      label: 'Early Bird Price',
    };
  }

  if (ticket?.is_late_registration && ticket?.is_late_registration_active) {
    return {
      price: Number(ticket?.late_registration_price ?? ticket?.config?.price ?? regularPrice),
      originalPrice: regularPrice,
      type: 'late_registration',
      label: 'Late Registration Price',
    };
  }

  return {
    price: Number(ticket?.config?.price ?? regularPrice),
    originalPrice: regularPrice,
    type: 'regular',
    label: '',
  };
}

function isTicketDateValid(ticket) {
  if (!ticket.start_date && !ticket.end_date) return true;

  const now = DateTime.now();

  const startDate = ticket.start_date
    ? DateTime.fromISO(ticket.start_date, { zone: props.eventData?.event_timezone })
    : null;
  const endDate = ticket.end_date
    ? DateTime.fromISO(ticket.end_date, { zone: props.eventData?.event_timezone })
    : null;

  if (startDate && endDate) {
    return now >= startDate && now <= endDate;
  } else if (startDate) {
    return now >= startDate;
  } else if (endDate) {
    return now <= endDate;
  }

  return true;
}

function isQtyValid(val) {
  const valueToTest = val === '' || val === null ? 0 : Number(val);

  if (Number.isNaN(valueToTest)) {
    return false;
  }

  for (const rule of rules.value) {
    const result = rule(valueToTest);
    if (result !== true) {
      return false;
    }
  }
  return true;
}

function displayTicketName(ticket) {
  let results = ticket.name;

  if (ticket.config?.price === 0) {
    return results;
  }

  if (ticket.price_type === 'free') {
    results += '';
    return results;
  }
  if (ticket.price_type === 'paid') {
    results += ` - ${Currency.formatCents(getActiveTicketPricing(ticket).price)}`;
    return results;
  }
  // if (ticket.price_type === "donation") {
  //   results += ` - Donations`;
  //   return results;
  // }
  // if (ticket.price_type === "gradual_discounts") {
  //   results += ` - Gradual Discounts! Starting at $${ticket.config?.discount_pattern?.split(",")?.[0]}`;
  //   return results;
  // }
  // results += ` - $${Currency.formatCents(ticket.config?.price)}`;
  return results;
}

function preventDecimal(e) {
  if (e.key === '.') {
    e.preventDefault();
  }
}

watch(localQty, (newLocalValue) => {
  const numericValue = Number(newLocalValue);

  if (numericValue === props.qty) {
    return;
  }

  if (!isQtyValid(numericValue)) {
    return;
  }

  emit('update:qty', numericValue, (acceptedValue) => {
    localQty.value = acceptedValue;
  });
});

function incrementQty() {
  let max = Number.MAX_SAFE_INTEGER;
  // A plain ticket's max_quantity is what is left to order. A bundle's is its raw limit, where 0
  // means unlimited; a bundle stops at `remaining` instead.
  if (!props.ticket?.table_id && typeof props.ticket?.max_quantity === 'number') {
    max = Math.min(max, props.ticket.max_quantity);
  }
  if (props.ticket?.table_id && typeof getRemainingHosts(props.ticket) === 'number') {
    max = Math.min(max, getRemainingHosts(props.ticket));
  }
  if (typeof props.ticket?.max_per_order === 'number') {
    max = Math.min(max, props.ticket.max_per_order);
  }
  if (localQty.value < max) {
    emit('update:qty', localQty.value + 1, (acceptedValue) => {
      localQty.value = acceptedValue;
    });
  }
}

function decrementQty() {
  const min = props.ticket?.min_per_order ?? 0;
  if (localQty.value > min) {
    emit('update:qty', localQty.value - 1, (acceptedValue) => {
      localQty.value = acceptedValue;
    });
  }
}

// FUN-43/FUN-46. This used to be `max_quantity - total_sold` worked out here, off
// a column a MySQL trigger maintains and never decrements on cancellation, and
// it could not see the event-capacity rule at all. The server sends the answer
// now; `remaining` is null when the bundle is uncapped.
const isSoldOut = computed(
  () => Boolean(props.ticket?.sold_out) || Boolean(props.ticket?.is_paused),
);

const getRemainingHosts = (ticket) => ticket?.remaining ?? null;

const rules = computed(() => [
  (val) => {
    const numVal = Number(val);
    const maxPerOrder = props.ticket?.max_per_order;

    if (typeof maxPerOrder === 'number') {
      return numVal > maxPerOrder
        ? `Can not order more than ${maxPerOrder} ${
            props.ticket.table_id
              ? props.eventData?.tableCustomLabel || 'table'
              : props.eventData?.ticketCustomLabel || 'ticket'
          } at a time.`
        : true;
    }
    return true;
  },
  (val) => {
    const numVal = Number(val);
    // Plain tickets only: a bundle's max_quantity is its raw limit (0 = unlimited), and how many
    // bundles are left is `remaining`.
    const maxQty = props.ticket?.table_id ? null : props.ticket?.max_quantity;

    if (typeof maxQty === 'number') {
      return numVal > maxQty
        ? `Can not order more than ${maxQty} ${
            props.ticket.table_id
              ? props.eventData?.tableCustomLabel || 'tables'
              : props.eventData?.ticketCustomLabel || 'tickets'
          } in total.`
        : true;
    }
    return true;
  },
  (val) => {
    const numVal = Number(val);
    if (numVal < 0) return 'Quantity cannot be negative.';
    if (numVal === 0) return true;

    const minPerOrder = props.ticket?.min_per_order;
    if (typeof minPerOrder === 'number') {
      return numVal < minPerOrder
        ? `Minimum quantity to be ordered for this ${
            props.ticket.table_id
              ? props.eventData?.tableCustomLabel || 'table'
              : props.eventData?.ticketCustomLabel || 'ticket'
          } is ${minPerOrder}.`
        : true;
    }
    return true;
  },
]);
</script>

<style scoped>
.disabled-ticket {
  cursor: not-allowed;
  background-color: rgba(0, 0, 0, 0.05);
}
</style>
